import type { SkillDetails } from "@amoreg/contracts";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { PencilIcon } from "lucide-react";
import { useActiveOrgId } from "../../state/orgs";
import type { SkillView } from "../../state/connections";
import {
  requestSkillDetails,
  SkillRequestError,
  skillDraft,
  skillDraftChanged,
  skillUpdate,
  type SkillDraft,
} from "../../state/skillDetails";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogPanel,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { SkillTile, type OpenSkill } from "./SkillRow";
import { getSkillSource, SKILL_SOURCE_LABEL } from "./skillContent";

/** One dialog per library; details are fetched only for the selected skill. */
export function SkillDialog({
  children,
  onSaved,
}: {
  readonly children: (openSkill: OpenSkill) => ReactNode;
  readonly onSaved?: (() => void) | undefined;
}) {
  const orgId = useActiveOrgId();
  const sequence = useRef(0);
  const [selected, setSelected] = useState<{
    skill: SkillView;
    trigger: HTMLElement;
    sequence: number;
  } | null>(null);
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discard, setDiscard] = useState(false);
  useEffect(() => {
    setOpen(false);
    setDirty(false);
    setSaving(false);
    setDiscard(false);
  }, [orgId]);
  const openSkill: OpenSkill = (skill, trigger) => {
    setSelected({ skill, trigger, sequence: ++sequence.current });
    setDirty(false);
    setSaving(false);
    setDiscard(false);
    setOpen(true);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next, event) => {
        if (!next && (saving || dirty)) {
          event.cancel();
          if (!saving) setDiscard(true);
          return;
        }
        setOpen(next);
      }}
    >
      {children(openSkill)}
      {selected && (
        <SkillDetailsContent
          key={`${orgId}:${selected.sequence}`}
          skill={selected.skill}
          orgId={orgId}
          open={open}
          trigger={selected.trigger}
          onDirty={setDirty}
          onSaving={setSaving}
          onSaved={onSaved}
          discard={discard}
          onKeepEditing={() => setDiscard(false)}
          onClose={() => {
            setDirty(false);
            setDiscard(false);
            setOpen(false);
          }}
        />
      )}
    </Dialog>
  );
}

function SkillDetailsContent({
  skill,
  orgId,
  open,
  trigger,
  onDirty,
  onSaving,
  onSaved,
  discard,
  onKeepEditing,
  onClose,
}: {
  readonly skill: SkillView;
  readonly orgId: string | null;
  readonly open: boolean;
  readonly trigger: HTMLElement;
  readonly onDirty: (dirty: boolean) => void;
  readonly onSaving: (saving: boolean) => void;
  readonly onSaved?: (() => void) | undefined;
  readonly discard: boolean;
  readonly onKeepEditing: () => void;
  readonly onClose: () => void;
}) {
  const formId = useId();
  const [detail, setDetail] = useState<SkillDetails | null>(null);
  const [draft, setDraft] = useState<SkillDraft | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saved, setSaved] = useState(false);
  const [revision, reload] = useState(0);
  const request = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError(null);
    setConflict(false);
    setEditing(false);
    setDetail(null);
    onDirty(false);
    if (!orgId) {
      setError("Choose an organisation to view this skill.");
      setLoading(false);
      return () => controller.abort();
    }
    void requestSkillDetails(orgId, skill.id, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        setDetail(value);
        setDraft(skillDraft(value));
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setError(
            cause instanceof SkillRequestError
              ? cause.message
              : "Could not load this skill. Please try again.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [orgId, skill.id, open, revision, onDirty]);

  const change = (field: keyof SkillDraft, value: string) => {
    if (!draft || !detail) return;
    const next = { ...draft, [field]: value };
    setDraft(next);
    onDirty(skillDraftChanged(next, detail));
    setSaved(false);
  };
  const cancel = () => {
    if (detail) setDraft(skillDraft(detail));
    setEditing(false);
    setError(null);
    setConflict(false);
    onDirty(false);
  };
  const save = async () => {
    const signal = request.current?.signal;
    if (!orgId || !detail || !draft || !signal || saving) return;
    let update;
    try {
      update = skillUpdate(draft, detail.version);
    } catch {
      setError(
        "Add a name, description and instructions within the field limits. Use at most 12 tags.",
      );
      return;
    }
    setSaving(true);
    onSaving(true);
    setError(null);
    try {
      const result = await requestSkillDetails(orgId, skill.id, signal, update);
      if (signal.aborted) return;
      setDetail(result);
      setDraft(skillDraft(result));
      setEditing(false);
      setConflict(false);
      setSaved(true);
      onDirty(false);
      onSaved?.();
    } catch (cause) {
      if (signal.aborted) return;
      setError(
        cause instanceof SkillRequestError
          ? cause.message
          : "Could not save this skill. Your draft is still here; try again.",
      );
      setConflict(cause instanceof SkillRequestError && cause.status === 409);
    } finally {
      if (!signal.aborted) {
        setSaving(false);
        onSaving(false);
      }
    }
  };
  const shown = detail ?? skill;
  return (
    <DialogPopup
      className="max-w-3xl [--app-scrollbar-width:4px]"
      finalFocus={() =>
        trigger.isConnected
          ? trigger
          : document.querySelector<HTMLElement>('[data-testid="connections-search"]')
      }
    >
      <DialogHeader>
        <div className="flex items-start gap-3 pr-6">
          <SkillTile name={shown.name} category={shown.category} className="size-11" />
          <div className="min-w-0">
            <DialogTitle className="break-words text-lg leading-6">{shown.name}</DialogTitle>
            <DialogDescription className="mt-1">
              {SKILL_SOURCE_LABEL[getSkillSource(shown)]}
              {shown.category ? ` · ${shown.category}` : ""}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>
      <DialogPanel className="space-y-5">
        {loading ? (
          <ListSkeleton label="Loading skill" />
        ) : detail && draft ? (
          editing ? (
            <form
              id={formId}
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <fieldset disabled={saving} className="space-y-4">
                <label className="block space-y-1.5 text-sm font-medium">
                  Name
                  <Input
                    aria-label="Name"
                    value={draft.name}
                    maxLength={120}
                    required
                    onChange={(e) => change("name", e.target.value)}
                  />
                </label>
                <label className="block space-y-1.5 text-sm font-medium">
                  When to use this skill
                  <Textarea
                    aria-label="When to use this skill"
                    value={draft.description}
                    maxLength={1024}
                    required
                    onChange={(e) => change("description", e.target.value)}
                  />
                </label>
                <label className="block space-y-1.5 text-sm font-medium">
                  Instructions
                  <Textarea
                    aria-label="Instructions"
                    className="min-h-52 font-mono text-xs leading-6"
                    value={draft.body}
                    maxLength={60000}
                    required
                    onChange={(e) => change("body", e.target.value)}
                  />
                  <span className="block text-xs font-normal text-muted-foreground">
                    Write the steps Amoreg should follow. Markdown is supported.
                  </span>
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block space-y-1.5 text-sm font-medium">
                    Category
                    <Input
                      aria-label="Category"
                      value={draft.category}
                      maxLength={128}
                      onChange={(e) => change("category", e.target.value)}
                    />
                  </label>
                  <label className="block space-y-1.5 text-sm font-medium">
                    Tags
                    <Input
                      aria-label="Tags"
                      value={draft.tags}
                      onChange={(e) => change("tags", e.target.value)}
                      placeholder="review, evidence"
                    />
                    <span className="block text-xs font-normal text-muted-foreground">
                      Separate tags with commas.
                    </span>
                  </label>
                </div>
              </fieldset>
            </form>
          ) : (
            <>
              <section>
                <h3 className="text-xs font-medium text-muted-foreground">When to use</h3>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6">
                  {detail.description}
                </p>
              </section>
              <section>
                <h3 className="mb-2 text-xs font-medium text-muted-foreground">Instructions</h3>
                <div className="space-y-3 break-words text-sm leading-6 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-muted [&_pre]:p-3 [&_a]:underline">
                  <ReactMarkdown
                    skipHtml
                    components={{
                      img: ({ alt }) => <span>{alt}</span>,
                      a: ({ children, href }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {detail.body}
                  </ReactMarkdown>
                </div>
              </section>
              <dl className="flex flex-wrap gap-x-6 gap-y-3 border-t pt-4 text-xs text-muted-foreground">
                <div>
                  <dt>Version</dt>
                  <dd className="mt-1 text-foreground">{detail.version}</dd>
                </div>
                <div>
                  <dt>Last updated</dt>
                  <dd className="mt-1 text-foreground">
                    {detail.updatedAt
                      ? new Date(detail.updatedAt).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "Built in"}
                  </dd>
                </div>
                {detail.tags.length > 0 && (
                  <div>
                    <dt>Tags</dt>
                    <dd className="mt-1 text-foreground">{detail.tags.join(", ")}</dd>
                  </div>
                )}
              </dl>
              {!detail.canEdit && (
                <p className="text-xs leading-5 text-muted-foreground">
                  {detail.scope === "organization"
                    ? "An organisation admin can edit this skill."
                    : "This shared skill is maintained by Amoreg. Organisation skills can be edited by your team's admins."}
                </p>
              )}
            </>
          )
        ) : null}
        {error && (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>{error}</p>
            {!detail && (
              <Button variant="outline" size="sm" onClick={() => reload((v) => v + 1)}>
                Try again
              </Button>
            )}
            {conflict && (
              <Button variant="outline" size="sm" onClick={() => reload((v) => v + 1)}>
                Discard draft and reload latest
              </Button>
            )}
          </div>
        )}
        {saved && (
          <p role="status" className="text-sm text-muted-foreground">
            Changes saved.
          </p>
        )}
      </DialogPanel>
      <DialogFooter>
        {discard ? (
          <div className="w-full space-y-3">
            <p role="alert" className="text-sm">
              Discard your unsaved changes?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onKeepEditing}>
                Keep editing
              </Button>
              <Button onClick={onClose}>Discard changes</Button>
            </div>
          </div>
        ) : editing ? (
          <>
            <Button variant="outline" disabled={saving} onClick={cancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              disabled={saving || !draft || !detail || !skillDraftChanged(draft, detail)}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {detail?.canEdit && (
              <Button
                onClick={() => {
                  setEditing(true);
                  setSaved(false);
                }}
              >
                <PencilIcon aria-hidden />
                Edit skill
              </Button>
            )}
          </>
        )}
      </DialogFooter>
    </DialogPopup>
  );
}
